import { useEffect, useRef, useState } from 'react';
import { ContentParser } from '../ContentParser';
import { TextContainer, ToggleButton, Wrapper } from './ExpandableText.styles';
import type { ExpandableTextProps } from './ExpandableText.types';

export function ExpandableText({
  text,
  maxLines = 4,
  showMoreLabel = 'Show more',
  showLessLabel = 'Show less',
}: ExpandableTextProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const checkOverflow = () => {
      // While clamped, scrollHeight > clientHeight indicates truncated content.
      setIsOverflowing(el.scrollHeight > el.clientHeight);
    };

    checkOverflow();

    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(checkOverflow) : null;

    observer?.observe(el);

    return () => observer?.disconnect();
  }, [text, maxLines]);

  return (
    <Wrapper>
      <TextContainer
        ref={textRef}
        data-clamped={!isExpanded ? 'true' : 'false'}
        style={{ '--max-lines': maxLines } as React.CSSProperties}
      >
        {(Array.isArray(text) ? text : [text]).map((block, index) => (
          <ContentParser key={index} text={block} />
        ))}
      </TextContainer>
      {(isOverflowing || isExpanded) && (
        <ToggleButton
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
        >
          {isExpanded ? showLessLabel : showMoreLabel}
        </ToggleButton>
      )}
    </Wrapper>
  );
}
